import { Observable } from "rxjs";

import { FlyerTemplate } from "../../../features/flyer-template/types/flyer-template/flyer-template.interface";
import { Canvas } from "../../../features/flyer-template/types/canvas/canvas.interface";
import { createGridFromTemplate } from "../../../features/flyer-template/types/grid/grid.functions";
import { FlyerTemplateMutation } from "../../../features/flyer-template/types/flyer-template/flyer-template-mutation.interface";
import { GridCellSelectionRange } from "../types/grid-cell-selection-range/grid-cell-selection-range.interface";
import { Range } from "../../../../../types/range/range";
import {
  createFlyerTemplatePosition,
  updatePosition,
} from "../../../features/flyer-template/types/flyer-template-position/flyer-template-position.functions";
import { FlyerTemplatePositionEditForm } from "../../../features/flyer-template/types/flyer-template-position-edit-form/flyer-template-position-edit-form.interface";
import * as http from "../../../../../services/http.service";
import { FlyerTemplateResponse } from "../../../features/flyer-template/types/flyer-template-response/flyer-template-response.interface";
import {
  FlyerTemplateRequest,
  FlyerTemplateRequestPosition,
} from "../../../features/flyer-template/types/flyer-template-request/flyer-template-request.interface";
import { FlyerTemplatePosition } from "../../../features/flyer-template/types/flyer-template-position/flyer-template-position.interface";

export function savePositionDetails(
  form: FlyerTemplatePositionEditForm,
  template: FlyerTemplate
): FlyerTemplate {
  return {
    ...template,
    positions: template.positions.map((position) =>
      position.id === form.id ? updatePosition(position, form) : position
    ),
  };
}

export function saveTemplate(template: FlyerTemplate): Observable<FlyerTemplateResponse> {
  if (template.id === -1) {
    return http.post("/page-templates/", mapTemplateToRequest(template));
  }

  return http.put(`/page-templates/${template.id}/`, mapTemplateToRequest(template));
}

export function setAspectRatio(aspectRatio: number, template: FlyerTemplate): FlyerTemplate {
  return {
    ...template,
    aspectRatio,
  };
}

export function movePosition(
  positionIndex: number,
  rows: Range,
  columns: Range,
  template: FlyerTemplate
): FlyerTemplate {
  const positions = [...template.positions];

  if (positionIndex > template.positions.length - 1) {
    throw new Error(`positionIndex ${positionIndex} out of bounds`);
  }

  positions[positionIndex].rows = rows;
  positions[positionIndex].columns = columns;

  return {
    ...template,
    positions,
  };
}

export function removePosition(positionId: number, template: FlyerTemplate): FlyerTemplate {
  return {
    ...template,
    positions: template.positions.filter((position) => position.id !== positionId),
  };
}

export function addPosition(
  selection: GridCellSelectionRange,
  template: FlyerTemplate
): FlyerTemplate {
  if (!selection.rows.isValid() || !selection.columns.isValid()) {
    return template;
  }

  return {
    ...template,
    positions: template.positions.concat([
      createFlyerTemplatePosition(selection.rows, selection.columns, template.positions),
    ]),
  };
}

export function addRowToTemplate(template: FlyerTemplate, canvas: Canvas): FlyerTemplateMutation {
  return updateTemplate(addRow(template), canvas);
}

export function addColumnToTemplate(
  template: FlyerTemplate,
  canvas: Canvas
): FlyerTemplateMutation {
  return updateTemplate(addColumn(template), canvas);
}

export function removeColumnFromTemplate(
  template: FlyerTemplate,
  canvas: Canvas
): FlyerTemplateMutation {
  return updateTemplate(removeColumn(template), canvas);
}

export function removeRowFromTemplate(
  template: FlyerTemplate,
  canvas: Canvas
): FlyerTemplateMutation {
  return updateTemplate(removeRow(template), canvas);
}

export function setTemplateColumns(
  columns: number,
  template: FlyerTemplate,
  canvas: Canvas
): FlyerTemplateMutation {
  return updateTemplate(setColumns(template, columns), canvas);
}

export function setTemplateRows(
  rows: number,
  template: FlyerTemplate,
  canvas: Canvas
): FlyerTemplateMutation {
  return updateTemplate(setRows(template, rows), canvas);
}

function areAllRowsConsumedByBlocks(template: FlyerTemplate) {
  return Boolean(template.positions.find((position) => position.rows.max === template.rows - 1));
}

function areAllColumnsConsumedByBlocks(template: FlyerTemplate) {
  return Boolean(
    template.positions.find((position) => position.columns.max === template.columns - 1)
  );
}

function addRow(template: FlyerTemplate): FlyerTemplate {
  return {
    ...template,
    rows: template.rows + 1,
  };
}

function addColumn(template: FlyerTemplate): FlyerTemplate {
  return {
    ...template,
    columns: template.columns + 1,
  };
}

function removeRow(template: FlyerTemplate): FlyerTemplate {
  if (areAllRowsConsumedByBlocks(template)) {
    return template;
  }

  return {
    ...template,
    rows: template.rows - 1,
  };
}

function removeColumn(template: FlyerTemplate): FlyerTemplate {
  if (areAllColumnsConsumedByBlocks(template)) {
    return template;
  }
  return {
    ...template,
    columns: template.columns - 1,
  };
}

function setRows(template: FlyerTemplate, rows: number): FlyerTemplate {
  return {
    ...template,
    rows,
  };
}

function setColumns(template: FlyerTemplate, columns: number): FlyerTemplate {
  return {
    ...template,
    columns,
  };
}

function updateTemplate(template: FlyerTemplate, canvas: Canvas): FlyerTemplateMutation {
  return {
    template,
    grid: createGridFromTemplate(template, canvas),
  };
}

function mapTemplateToRequest(template: FlyerTemplate): FlyerTemplateRequest {
  const { positions, ...rest } = template;

  return {
    ...rest,
    page_template_positions: mapTemplatePositionsToRequest(positions),
  };
}

function mapTemplatePositionsToRequest(
  positions: FlyerTemplatePosition[]
): FlyerTemplateRequestPosition[] {
  return positions.map((position) => {
    const { type, id, ...rest } = position;

    return {
      ...rest,
      block_type: type,
      columns: mapTemplatePositionRangeToRequest(position.columns),
      rows: mapTemplatePositionRangeToRequest(position.rows),
    };
  });
}

function mapTemplatePositionRangeToRequest(range: Range): string {
  return JSON.stringify({
    lower: String(range.min),
    upper: String(range.max + 1),
  });
}
